import type { DestroyRef, WritableSignal } from '@angular/core';
import { inject, Injectable, Injector, runInInjectionContext, signal, untracked } from '@angular/core';
import type { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { createSectionForm, type SectionForm } from '@sites/entity-signal-form';

import type { NormalizedSectionEntity } from '@web/types';

import { HistoryFacade } from '@web/store/history/history.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';

type SectionFormEntry = {
    form: SectionForm;
    entitySignal: WritableSignal<NormalizedSectionEntity>;
    storeSub: Subscription;
    refCount: number;
};

/** The section fields a form edit can touch — the envelope stays the store's. */
const EDITABLE_KEYS = ['props'] as const satisfies readonly (keyof NormalizedSectionEntity)[];

const pickEditable = (section: NormalizedSectionEntity | undefined): Partial<NormalizedSectionEntity> => {
    if (!section) return {};

    const changes: Record<string, unknown> = {};
    for (const key of EDITABLE_KEYS) {
        if (key in section) {
            changes[key] = (section as Record<string, unknown>)[key];
        }
    }

    return changes as Partial<NormalizedSectionEntity>;
};

/**
 * Ref-counted registry of section signal forms, the sibling of
 * {@link WebGridFormService} / {@link WebTabFormService}. The form drives the
 * persisted section JSON directly (v0 — see the `section` entity in
 * `@sites/entity-core`); the store keeps owning identity, the block link and
 * the column list, so only `props` is dispatched back.
 */
@Injectable({ providedIn: 'root' })
export class WebSectionFormService {
    private readonly injector = inject(Injector);
    private readonly sectionsFacade = inject(SectionsFacade);
    private readonly historyFacade = inject(HistoryFacade);

    private readonly registry = new Map<string, SectionFormEntry>();

    public getOrCreate(sectionId: string): SectionForm {
        const existing = this.registry.get(sectionId);
        if (existing) {
            existing.refCount++;
            return existing.form;
        }

        const entitySignal = signal<NormalizedSectionEntity>(undefined as any);

        // NgRx store.select() emits synchronously, so entitySignal is populated immediately
        const storeSub = this.sectionsFacade
            .sectionById$(sectionId)
            .pipe(filter(Boolean))
            .subscribe((value) => entitySignal.set(value));

        const initialEntity = entitySignal();

        const form = runInInjectionContext(this.injector, () =>
            createSectionForm({
                entity: entitySignal,
                // The dispatcher picks the subtype factory off the entity signal,
                // which the store has already populated synchronously.
                onUpdate: (value: NormalizedSectionEntity) => {
                    const changes = pickEditable(value);

                    // A batch entry, not a change of its own: the v2 collector keys
                    // entries by entity, keeps the first `before` and the latest
                    // `after`, and flushes them together — so a slider drag is one
                    // undo step, and an edit that touches the row and its columns at
                    // once is undone as one thing.
                    this.historyFacade.addBatchUpdateToHistory(
                        'section',
                        { id: value.id, changes: pickEditable(untracked(entitySignal)) },
                        { id: value.id, changes }
                    );

                    this.sectionsFacade.updateSectionProperties(value.id, changes);
                },
            })
        );

        // Sync immediately with the store value to avoid an empty model before the effect fires
        if (initialEntity) {
            form.syncFromEntity(structuredClone(initialEntity) as never);
        }

        this.registry.set(sectionId, { form, entitySignal, storeSub, refCount: 1 });
        return form;
    }

    public getOrCreateScoped(sectionId: string, destroyRef: DestroyRef): SectionForm {
        const form = this.getOrCreate(sectionId);
        destroyRef.onDestroy(() => this.release(sectionId));
        return form;
    }

    public getForm(sectionId: string): SectionForm | null {
        return this.registry.get(sectionId)?.form ?? null;
    }

    public release(sectionId: string): void {
        const entry = this.registry.get(sectionId);
        if (!entry) {
            return;
        }

        entry.refCount--;
        if (entry.refCount <= 0) {
            this.deleteForm(sectionId);
        }
    }

    public deleteForm(sectionId: string): void {
        const entry = this.registry.get(sectionId);
        if (!entry) {
            return;
        }

        entry.storeSub.unsubscribe();
        entry.form.destroy();
        this.registry.delete(sectionId);
    }
}
